// --------------------- Import Globais ---------------------
import { useState, useEffect, useMemo } from "react";
import { useMediaQuery } from "@mui/material";
// --------------------- Import Antd-Design ---------------------
import {
  Col,
  Flex,
  Row,
  Button,
  MenuProps,
  Skeleton,
  Table,
  Typography,
} from "antd";
import type { TableProps } from "antd/es/table";
// --------------------- Import Material Icon ---------------------
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";

// --------------------- Import Hooks ---------------------
import { useBuscarAnuncios } from "./hooks/useBuscarAnuncios";
// --------------------- Import Styles ---------------------
import { Container } from "./styles";
// --------------------- Import Uteis ---------------------
import { usePermissao } from "../../uteis/permissaoAdm/usePermissao";
// --------------------- Import Components ---------------------
import { Columns } from "./components/Columns";
import ModalDesativar from "./components/ModalDesativar";
import ModalExcluir from "./components/ModalExcluir";
import ModalAdicionar from "./components/ModalAdicionar";
import ModalExibirAnuncio from "./components/ModalExibirAnuncio";
import { useMessage } from "../../uteis/mensagens/useMessage";
import Globais from "../../components/Globais";
import { buscarCotaUsuarioPorAnuncioService } from "../../services/buscarAnunciosService";

export default function ViewGerenciarAnuncios() {
  // Traz o estilo Title da biblioteca
  const { Title } = Typography;
  const isMobile = useMediaQuery("(max-width:600px)");

  // Verificação de permissão do usuário, NDA = 0 - SEM PERMISSÃO, NDA = 1 - COM PERMISSÃO
  // Definação do Loading caso seja permitido
  const { isLoading: isTokenLoading, isValid } = usePermissao("OPE");

  const { anuncios, isLoading, buscar } = useBuscarAnuncios();

  const [anuncioId, setAnuncioId] = useState<number>();

  const [active, setActive] = useState<string>("");

  // Controle de Paginação
  const [currentPage, setCurrentPage] = useState(1);

  // Adicionar Anúncio
  const [openModalAdicionar, setOpenModalAdicionar] = useState<boolean>(false);

  const handleModalAdicionar = () => {
    setOpenModalAdicionar(!openModalAdicionar);
  };

  // Desativar Anúncio
  const [openModalDesativar, setOpenModalDesativar] = useState<boolean>(false);

  const handleModalDesativar = () => {
    setOpenModalDesativar(!openModalDesativar);
  };

  // Excluir Anúncio
  const [openModalExcluir, setOpenModalExcluir] = useState<boolean>(false);

  const handleModalExcluir = () => {
    setOpenModalExcluir(!openModalExcluir);
  };

  // Exibir Anúncio
  const [openModalVisualizar, setOpenModalVisualizar] =
    useState<boolean>(false);

  const handleModalVisualizar = () => {
    setOpenModalVisualizar(!openModalVisualizar);
  };



  // Inicializando o filtro
  const [filteredInfo, setFilteredInfo] = useState<{
  proprietario?: string[] | null;
  celular?: string[] | null; // ✅
  empreendimento?: string[] | null;
  dataAnuncio?: string[] | null;
  dataInativacaoAnuncio?: string[] | null;
  active?: string[] | null;
}>({
  proprietario: null,
  celular: null, // ✅
  empreendimento: null,
  dataAnuncio: null,
  dataInativacaoAnuncio: null,
  active: null,
});

  const [semFiltros, setSemFiltros] = useState(true);

  useEffect(() => {
    const filtros = localStorage.getItem("filtrosPropriedades");
    const pagina = localStorage.getItem("pageGerPropriedades");

    if (filtros) {
      const novoFiltro = JSON.parse(filtros);
      setFilteredInfo({
        proprietario: novoFiltro.proprietario
          ? [novoFiltro.proprietario]
          : null,
        celular: novoFiltro.celular
          ? [novoFiltro.celular]
          : null,
        empreendimento: novoFiltro.empreendimento
          ? [novoFiltro.empreendimento]
          : null,
        dataAnuncio: novoFiltro.dataAnuncio ? [novoFiltro.dataAnuncio] : null,
        dataInativacaoAnuncio: novoFiltro.dataInativacaoAnuncio
          ? [novoFiltro.dataInativacaoAnuncio]
          : null,
        active: novoFiltro.active ? [novoFiltro.active] : null,
      });
      setSemFiltros(false);
    }

    if (pagina) {
      setCurrentPage(Number(pagina));
    }
  }, []);

  const onChange: TableProps<any>["onChange"] = (pagination, filters) => {
    setFilteredInfo({
      proprietario: filters.proprietario as string[] | null,
      celular: filters.celular as string[] | null,
      empreendimento: filters.empreendimento as string[] | null,
      dataAnuncio: filters.dataAnuncio as string[] | null,
      dataInativacaoAnuncio: filters.dataInativacaoAnuncio as string[] | null,
      active: filters.active as string[] | null,
    });

    const hasFilters = Boolean(
      filters.proprietario ||
        filters.empreendimento ||
        filters.dataAnuncio ||
        filters.dataInativacaoAnuncio ||
        filters.active||
        filters.celular
    );
    setSemFiltros(!hasFilters);

    if (pagination.current) {
      localStorage.setItem(
        "pageGerPropriedades",
        pagination.current.toString()
      );
    }

    localStorage.setItem("filtrosPropriedades", JSON.stringify(filters));
  };

  const removerFiltros = () => {
    localStorage.removeItem("filtrosPropriedades");
    localStorage.removeItem("pageGerPropriedades");

    setFilteredInfo({
      proprietario: null,
      celular: null,
      empreendimento: null,
      dataAnuncio: null,
      dataInativacaoAnuncio: null,
      active: null,
    });
    setSemFiltros(true);
    setCurrentPage(1);
  };

  const handleFilter = () => {
    removerFiltros();
  };

  // Mensagens
  const { showWarning, contextHolder } = useMessage();

  // OnClick das opções e funções que serão realizadas do item Ações
  const onClick: MenuProps["onClick"] = async ({ key }) => {
    switch (key) {
      //Exibir Anúncio
      case "1":
        if (active === "Sim") {
          showWarning("Este anúncio já está sendo exibido!");
          return;
        }
        handleModalVisualizar();
        break;
      //Desativar
      case "2":
        if (active === "Não") {
          showWarning("Este anúncio já está oculto!");
          return;
        }
        handleModalDesativar();
        break;
      // Excluir
      case "3":
          if (!anuncioId) {
    showWarning("Nenhum anúncio selecionado!");
    return;
  }

  try {
    // 🔹 Busca o cotaUsuarioId via backend
    const result = await buscarCotaUsuarioPorAnuncioService(anuncioId);

    if (result?.cotaUsuarioId) {
      // 🔹 Redireciona para página de detalhes com o ID da cota
      window.open(
        `${Globais.DOMINIOANUNCIO}/detalhes.html?id=${result.cotaUsuarioId}`,
        "_blank"
      );
    } else {
      showWarning("Não foi possível encontrar a cota deste anúncio.");
    }
  } catch (error) {
    console.error("Erro ao redirecionar para detalhes:", error);
    showWarning("Erro ao buscar informações do anúncio.");
  }
        break;
    }
  };

  // Traz as informações  OnClick, origem , nome e id do apartamento além do nome do empreendimento, ao clickar na linha especifica
  const columns = Columns(onClick, setAnuncioId, setActive, {
    proprietario: filteredInfo.proprietario,
     celular: filteredInfo.celular, 
    empreendimento: filteredInfo.empreendimento,
    dataAnuncio: filteredInfo.dataAnuncio,
    dataInativacaoAnuncio: filteredInfo.dataInativacaoAnuncio,
    active: filteredInfo.active,
  });
  return (
    <>
      {(isLoading && isTokenLoading) || !isValid ? (
        <Skeleton active />
      ) : (
        <>
          {contextHolder}
          <Container>
            <Row>
              <Col span={5}>
               
              </Col>
              <Col span={12}>
                <Flex align="center" justify="center">
                  <HomeIcon color="primary" fontSize="large" />
                  <Title
                    style={{
                      marginLeft: 10,
                      marginTop: 10,
                      fontSize: isMobile ? "1rem" : "2rem",
                      color: "rgb(0, 54, 68)",
                    }}
                    level={3}
                  >
                    Gerenciar Anúncios
                  </Title>
                </Flex>
              </Col>
            </Row>

            <Row>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  height: "auto",
                  width: "90%",
                  gap: "20px",
                }}
              >
                <div style={{ display: "flex", gap: "20px" }}>
                  <Button
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "auto",
                    }}
                    onClick={handleModalAdicionar}
                    type="primary"
                  >
                    + Anuncio
                  </Button>

                  {semFiltros ? (
                    <Button onClick={handleFilter} type="primary" disabled>
                      <FilterAltIcon />
                    </Button>
                  ) : (
                    <Button onClick={handleFilter} type="primary">
                      <FilterAltOffIcon />
                    </Button>
                  )}
                </div>
              </div>
              <Col span={isMobile ? 5 : 1}></Col>

              <Col
                span={22}
                style={{
                  marginTop: isMobile ? 15 : 15,
                  marginBottom: isMobile ? 30 : 50,
                }}
              >
                <Flex
                  style={{
                    width: isMobile ? "auto" : "100%",
                    overflow: "auto",
                    marginLeft: 30,
                  }}
                >
                  <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={anuncios}
                    onChange={onChange}
                    bordered
                    style={{
                      alignItems: "center",
                      justifyContent: "center",
                      width: isMobile ? "auto" : "100%",
                      maxWidth: isMobile ? "none" : "100%",
                    }}
                    pagination={{ defaultCurrent: currentPage }}
                  />
                </Flex>
              </Col>

              <Col span={1}></Col>
            </Row>
            <ModalAdicionar
              isModalOpen={openModalAdicionar}
              handleFechar={handleModalAdicionar}
              anuncioId={anuncioId!} // Diz ao TypeScript que anuncioId não é null/undefined (não faz checagem em runtime)
              buscar={buscar}
            />
            <ModalDesativar
              isModalOpen={openModalDesativar}
              handleFechar={handleModalDesativar}
              anuncioId={anuncioId!} // Diz ao TypeScript que anuncioId não é null/undefined (não faz checagem em runtime)
              buscar={buscar}
            />
            <ModalExcluir
              isModalOpen={openModalExcluir}
              handleFechar={handleModalExcluir}
              anuncioId={anuncioId!} // Diz ao TypeScript que anuncioId não é null/undefined (não faz checagem em runtime)
              buscar={buscar}
            />
            <ModalExibirAnuncio
              isModalOpen={openModalVisualizar}
              handleFechar={handleModalVisualizar}
              anuncioId={anuncioId!} // Diz ao TypeScript que anuncioId não é null/undefined (não faz checagem em runtime)
              buscar={buscar}
            />
          </Container>
        </>
      )}
    </>
  );
}
